import { useState, useCallback, useEffect, useRef } from 'react';
import { useToast } from './use-toast';
import { supabase } from '../lib/supabase';
import type { Media, MediaCreate, MediaUpdate } from '@/types';

interface UseMediasReturn {
  medias: Media[];
  loading: boolean;
  fetchMedias: () => Promise<void>;
  refetch: () => Promise<void>; // Adicionar refetch na interface
  createMedia: (media: MediaCreate) => Promise<Media | null>;
  updateMedia: (id: string, media: MediaUpdate) => Promise<Media | null>;
  deleteMedia: (id: string) => Promise<boolean>;
}

export const useMedias = (userIdOrEmail?: string, options?: { ignoreStorageFallback?: boolean }): UseMediasReturn => {
  const [medias, setMedias] = useState<Media[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const lastUserIdRef = useRef<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Priorizar userIdOrEmail passado por parâmetro (Dashboard), depois tentar sessionStorage (Player)
  const getUserId = useCallback(() => {
    // Priorizar o userId passado explicitamente (Dashboard)
    if (userIdOrEmail) {
      console.log('✅ useMedias - Usando userId do parâmetro (Dashboard):', userIdOrEmail);
      return userIdOrEmail;
    }

    // Se a opção ignoreStorageFallback for true, NÃO tenta ler do sessionStorage
    // Isso é crucial para o Player que deve obedecer estritamente à ativação atual
    if (options?.ignoreStorageFallback) {
      console.log('🚫 useMedias - Storage Fallback desativado explicitamente pelo consumidor');
      return null;
    }

    // Verificar se é modo preview do dashboard (iframe com user_id na URL)
    const previewUserId = sessionStorage.getItem('player_preview_user_id');
    if (previewUserId) {
      console.log('🎬 useMedias - Usando userId do PREVIEW (Dashboard Modal):', previewUserId);
      return previewUserId;
    }

    // Fallback: tentar obter o userId do usuário ativado (Player real)
    const activatedUserId = sessionStorage.getItem('player_activated_user_id');
    if (activatedUserId) {
      console.log('✅ useMedias - Usando userId do usuário ativado (Player):', activatedUserId);
      return activatedUserId;
    }

    console.log('⚠️ useMedias - Nenhum userId disponível');
    return null;
  }, [userIdOrEmail, options?.ignoreStorageFallback]);

  const fetchMedias = useCallback(async () => {
    const userId = getUserId();

    // Verificar se foi ativado via Companion (motorista/parceiro)
    const isActivatedByCompanion = sessionStorage.getItem('player_activated_by_companion') === 'true';

    // Detectar modo impersonate via URL
    const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const impersonateUserId = urlParams?.get('impersonate');
    const isImpersonating = !!impersonateUserId && !!userIdOrEmail;

    if (!userId && !isActivatedByCompanion && !isImpersonating) {
      console.log('⚠️ useMedias - Sem userId disponível e não é Companion/Impersonate');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      let data: any[] | null = null;
      let error: any = null;

      if (isActivatedByCompanion) {
        // MODO COMPANION/MOTORISTA: Buscar TODAS as mídias ativas do sistema
        console.log('🌐 useMedias - Modo Companion: Buscando TODAS as mídias ativas do sistema');

        const result = await supabase
          .from('medias')
          .select('*, order_index')
          .eq('active', true)
          .order('order_index', { ascending: true })
          .order('created_at', { ascending: true });

        data = result.data;
        error = result.error;
      } else if (isImpersonating && userId) {
        // MODO IMPERSONATE: Usar RPC admin para bypassar RLS
        console.log('👤 useMedias - Modo Impersonate: Buscando mídias via RPC admin para userId:', userId);

        const result = await supabase.rpc('admin_get_user_medias', { p_user_id: userId });
        data = result.data;
        error = result.error;

        if (error) {
          console.warn('⚠️ useMedias - Falha ao usar RPC admin, tentando query direta:', error);
          // Fallback para query direta
          const fallbackResult = await supabase
            .from('medias')
            .select('*, order_index')
            .eq('user_id', userId)
            .eq('active', true)
            .order('order_index', { ascending: true })
            .order('created_at', { ascending: true });

          data = fallbackResult.data;
          error = fallbackResult.error;
        }
      } else {
        // MODO NORMAL: Buscar mídias do usuário específico
        console.log('🔍 useMedias - Buscando mídias para userId:', userId);

        const result = await supabase
          .from('medias')
          .select('*, order_index')
          .eq('user_id', userId)
          .eq('active', true)
          .order('order_index', { ascending: true })
          .order('created_at', { ascending: true });

        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      const newMediasCount = data?.length || 0;
      const currentMediasCount = medias.length;

      console.log('✅ useMedias - Mídias carregadas:', newMediasCount);
      console.log('📊 useMedias - Modo:',
        isActivatedByCompanion ? 'COMPANION (todas as mídias)' :
          isImpersonating ? 'IMPERSONATE (usuário específico via admin)' :
            'NORMAL (por usuário)'
      );
      console.log('📊 useMedias - Comparação:', {
        anterior: currentMediasCount,
        atual: newMediasCount,
        diferenca: newMediasCount - currentMediasCount
      });

      // Log detalhado dos selected_screens
      console.log('📋 useMedias - Lista de mídias (DADOS BRUTOS):', data?.map(m => ({
        id: m.id,
        title: m.title,
        user_id: m.user_id,
        selected_screens: m.selected_screens,
        selected_screens_type: typeof m.selected_screens,
        selected_screens_isArray: Array.isArray(m.selected_screens),
        selected_screens_raw: JSON.stringify(m.selected_screens)
      })));

      setMedias(data || []);
      lastUserIdRef.current = userId || 'companion_mode';
    } catch (error) {
      console.error('❌ Erro ao carregar mídias:', error);
      toast({
        title: 'Erro ao carregar mídias',
        description: 'Não foi possível carregar as mídias. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [getUserId, toast, userIdOrEmail, medias.length]);

  const createMedia = async (media: MediaCreate) => {
    try {
      console.log('🎬 useMedias - Criando mídia com dados:', media);

      // Detectar modo impersonate
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const impersonateUserId = urlParams?.get('impersonate');
      const isImpersonating = !!impersonateUserId;

      let data: any = null;
      let error: any = null;

      if (isImpersonating) {
        // Modo Admin: usar RPC para criar mídia em nome do usuário impersonado
        console.log('👑 useMedias - Modo Admin Impersonate: Criando mídia via RPC admin');
        const result = await supabase.rpc('admin_create_media', {
          p_user_id: media.user_id,
          p_title: media.title,
          p_url: media.url,
          p_type: media.type || 'link',
          p_display_time: media.display_time || 5000,
          p_selected_screens: media.selected_screens || [],
          p_active: media.active !== false,
          p_organization_id: media.organization_id || null
        });

        if (result.error) throw result.error;

        const response = result.data as { success: boolean; data?: any; error?: string };
        if (!response.success) {
          throw new Error(response.error || 'Erro ao criar mídia via admin');
        }

        // Buscar dados frescos do banco para garantir formato consistente
        if (response.data?.id) {
          const { data: freshData, error: fetchError } = await supabase
            .from('medias')
            .select('*')
            .eq('id', response.data.id)
            .single();

          data = fetchError ? response.data : freshData;
        } else {
          data = response.data;
        }
      } else {
        // Modo normal: inserir diretamente
        const result = await supabase
          .from('medias')
          .insert([media])
          .select()
          .single();

        if (result.error) throw result.error;
        data = result.data;
      }

      // Atualizar estado local
      if (data) {
        setMedias(prev => [...prev, data]);
      }

      toast({
        title: 'Mídia criada',
        description: 'A mídia foi criada com sucesso.',
      });

      // Disparar evento para notificar outros componentes
      window.dispatchEvent(new CustomEvent('mediaCreated', { detail: { mediaId: data?.id } }));

      return data;
    } catch (error) {
      console.error('Erro ao criar mídia:', error);
      toast({
        title: 'Erro ao criar mídia',
        description: 'Não foi possível criar a mídia. Tente novamente.',
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateMedia = async (id: string, media: MediaUpdate) => {
    try {
      console.log('🔄 [UPDATE MEDIA] Dados recebidos para atualização:', {
        id,
        media,
        selected_screens_recebido: media.selected_screens,
        selected_screens_tipo: typeof media.selected_screens,
        selected_screens_isArray: Array.isArray(media.selected_screens),
        selected_screens_raw: JSON.stringify(media.selected_screens)
      });

      // Detectar modo impersonate
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const impersonateUserId = urlParams?.get('impersonate');
      const isImpersonating = !!impersonateUserId;

      let data: any = null;
      let error: any = null;

      if (isImpersonating) {
        // Modo Admin: usar RPC para atualizar mídia
        console.log('👑 useMedias - Modo Admin Impersonate: Atualizando mídia via RPC admin');
        console.log('👑 useMedias - Parâmetros enviados:', {
          p_media_id: id,
          p_title: media.title || null,
          p_url: media.url || null,
          p_type: media.type || null,
          p_display_time: media.display_time || null,
          p_selected_screens: media.selected_screens || null,
          p_active: media.active !== undefined ? media.active : null,
          p_position_config: media.position_config || null
        });

        const result = await supabase.rpc('admin_update_media', {
          p_media_id: id,
          p_title: media.title || null,
          p_url: media.url || null,
          p_type: media.type || null,
          p_display_time: media.display_time || null,
          p_selected_screens: media.selected_screens || null,
          p_active: media.active !== undefined ? media.active : null,
          p_position_config: media.position_config || null
        });

        console.log('👑 useMedias - Resposta da RPC:', result);

        if (result.error) {
          console.error('❌ useMedias - Erro na RPC admin_update_media:', result.error);
          throw result.error;
        }

        const response = result.data as { success: boolean; data?: any; error?: string };
        console.log('👑 useMedias - Response parseado:', response);

        if (!response.success) {
          console.error('❌ useMedias - RPC retornou success=false:', response.error);
          throw new Error(response.error || 'Erro ao atualizar mídia via admin');
        }

        // Usar os dados retornados pela RPC diretamente
        // A RPC retorna os dados completos da mídia atualizada via row_to_json
        data = response.data;
        console.log('👑 useMedias - Dados da mídia atualizada:', data);
      } else {
        // Modo normal: atualizar diretamente
        const result = await supabase
          .from('medias')
          .update(media)
          .eq('id', id)
          .select()
          .single();

        if (result.error) throw result.error;
        data = result.data;
      }

      console.log('✅ [UPDATE MEDIA] Mídia atualizada no banco:', {
        data_completo: data,
        selected_screens_salvo: data?.selected_screens,
        selected_screens_tipo: typeof data?.selected_screens,
        selected_screens_isArray: Array.isArray(data?.selected_screens),
        selected_screens_raw: JSON.stringify(data?.selected_screens)
      });

      // Atualizar estado local imediatamente
      if (data) {
        setMedias(prev => prev.map(m => m.id === id ? { ...m, ...data } : m));
      }

      // Em modo impersonate, forçar refetch para garantir sincronização
      if (isImpersonating && impersonateUserId) {
        console.log('👑 useMedias - Forçando refetch após atualização no modo impersonate');
        // Aguardar um pouco para o banco processar a atualização
        setTimeout(async () => {
          try {
            const result = await supabase.rpc('admin_get_user_medias', { p_user_id: impersonateUserId });
            if (result.data && !result.error) {
              setMedias(result.data);
              console.log('👑 useMedias - Mídias refetchadas com sucesso');
            }
          } catch (e) {
            console.warn('⚠️ useMedias - Erro no refetch:', e);
          }
        }, 500);
      }

      toast({
        title: 'Mídia atualizada',
        description: 'A mídia foi atualizada com sucesso.',
      });

      // Disparar evento para notificar outros componentes
      window.dispatchEvent(new CustomEvent('mediaUpdated', { detail: { mediaId: id, data } }));

      return data;
    } catch (error) {
      console.error('Erro ao atualizar mídia:', error);
      toast({
        title: 'Erro ao atualizar mídia',
        description: 'Não foi possível atualizar a mídia. Tente novamente.',
        variant: 'destructive',
      });
      return null;
    }
  };

  const deleteMedia = async (id: string) => {
    try {
      // Detectar modo impersonate
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const impersonateUserId = urlParams?.get('impersonate');
      const isImpersonating = !!impersonateUserId;

      if (isImpersonating) {
        // Modo Admin: usar RPC para deletar mídia
        console.log('👑 useMedias - Modo Admin Impersonate: Deletando mídia via RPC admin');
        const result = await supabase.rpc('admin_delete_media', {
          p_media_id: id
        });

        if (result.error) throw result.error;

        const response = result.data as { success: boolean; error?: string };
        if (!response.success) {
          throw new Error(response.error || 'Erro ao deletar mídia via admin');
        }
      } else {
        // Modo normal: deletar diretamente
        const { error } = await supabase
          .from('medias')
          .delete()
          .eq('id', id);

        if (error) throw error;
      }

      // Atualizar estado local
      setMedias(prev => prev.filter(m => m.id !== id));

      toast({
        title: 'Mídia excluída',
        description: 'A mídia foi excluída com sucesso.',
      });

      return true;
    } catch (error) {
      console.error('Erro ao excluir mídia:', error);
      toast({
        title: 'Erro ao excluir mídia',
        description: 'Não foi possível excluir a mídia. Tente novamente.',
        variant: 'destructive',
      });
      return false;
    }
  };

  // useEffect para carregar mídias na inicialização
  useEffect(() => {
    console.log('🔄 useMedias - useEffect inicial executado');
    fetchMedias();
  }, [fetchMedias]); // Incluir fetchMedias nas dependências

  // useEffect para recarregar mídias quando userIdOrEmail muda
  useEffect(() => {
    const userId = getUserId();
    console.log('🔄 useMedias - useEffect userIdOrEmail executado com userId:', userId);
    if (userId && userId !== lastUserIdRef.current) {
      console.log('🔄 useMedias - Recarregando mídias após mudança de userId:', userId);
      fetchMedias();
    }
  }, [userIdOrEmail, getUserId, fetchMedias]); // Incluir todas as dependências necessárias

  // useEffect para escutar eventos de atualização de ordem (drag & drop)
  useEffect(() => {
    const handleMediaOrderUpdate = (event: CustomEvent) => {
      const { type, data } = event.detail;

      if (type === 'media_order_updated' && data) {
        console.log('🔄 useMedias - Atualizando ordem local das mídias via evento');
        setMedias(data);
      }
    };

    window.addEventListener('dashboardDataUpdate', handleMediaOrderUpdate as EventListener);

    return () => {
      window.removeEventListener('dashboardDataUpdate', handleMediaOrderUpdate as EventListener);
    };
  }, []);

  // useEffect para detectar mudanças no sessionStorage (após ativação do dispositivo)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedUserId = sessionStorage.getItem('player_activated_user_id');
      console.log('🔄 useMedias - Detectada mudança no storage, userId:', storedUserId);
      if (storedUserId && storedUserId !== lastUserIdRef.current) {
        console.log('🔄 useMedias - userId mudou, recarregando mídias...');
        fetchMedias();
      }
    };

    // Listener para eventos de storage (quando outra aba muda o storage)
    window.addEventListener('storage', handleStorageChange);

    // Listener customizado para quando o mesmo componente muda o storage
    const handlePlayerActivated = () => {
      console.log('🔄 useMedias - Evento playerActivated recebido');
      handleStorageChange();
    };
    window.addEventListener('playerActivated', handlePlayerActivated);

    // Verificar periodicamente se o userId mudou (fallback para casos onde eventos não disparam)
    const checkInterval = setInterval(() => {
      const storedUserId = sessionStorage.getItem('player_activated_user_id');
      if (storedUserId && storedUserId !== lastUserIdRef.current && !userIdOrEmail) {
        console.log('🔄 useMedias - Polling detectou novo userId:', storedUserId);
        fetchMedias();
      }
    }, 1000); // Verificar a cada 1 segundo

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('playerActivated', handlePlayerActivated);
      clearInterval(checkInterval);
    };
  }, [fetchMedias, userIdOrEmail]);

  // useEffect para configurar polling no contexto do player
  useEffect(() => {
    const userId = getUserId();

    // Só ativar polling se estivermos no player (não no dashboard)
    const isPlayerContext = window.location.pathname.includes('/player');

    if (isPlayerContext && userId) {
      console.log('🔄 useMedias - Configurando polling para player com userId:', userId);

      // Limpar interval anterior se existir
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // Configurar polling a cada 30 segundos para verificar novas mídias
      pollingIntervalRef.current = setInterval(() => {
        console.log('🔄 useMedias - Polling: verificando novas mídias...');
        fetchMedias();
      }, 30000);

      // Cleanup
      return () => {
        if (pollingIntervalRef.current) {
          console.log('🔄 useMedias - Limpando polling interval');
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [userIdOrEmail, fetchMedias]); // Dependências para reconfigurar o polling se necessário

  return {
    medias,
    loading,
    fetchMedias,
    refetch: fetchMedias, // Adicionar alias para compatibilidade
    createMedia,
    updateMedia,
    deleteMedia,
  };
};

// Função para buscar mídias filtrando pelo título
export const fetchMediasByQuery = async (query: string, userId: string): Promise<Media[]> => {
  if (!userId) return [];
  try {
    // Buscar mídias filtrando pelo título (case-insensitive)
    const { data, error } = await supabase
      .from('medias')
      .select('*, order_index')
      .eq('user_id', userId)
      .eq('active', true)
      .ilike('title', `%${query}%`)
      .order('order_index', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Erro ao buscar mídias por query:', error);
    return [];
  }
};
